import { useEffect, useMemo, useRef, useState } from 'react';
import { RegistrationModalShell, MultiSelect, SingleRangeSlider, TuitionModeSelector, WeeklyTimeTable } from '@tutor/ui';
import { toast } from 'sonner';
import { STATE_DISTRICTS, STATE_OPTIONS } from '@tutor/types';
import { createCase } from '@/api/services/casesApi';
import { useAuthContext } from '@/contexts/useAuthContext';
import { useModal } from '@/contexts/useModal';
import { getErrorMessage } from '@/api/errorHandler';
import { clearPendingRegistration, getPendingRegistration, savePendingRegistration } from '@/utils/storage';
import { parseMusicParentStep } from '@/schemas/musicRegistrationSchemas';
import type { MusicParentRegistrationFormData } from '@/types/musicRegistration';
import TooltipIcon from '@/components/common/TooltipIcon';
import {
  MUSIC_EXAM_BOARDS, MUSIC_INSTRUMENTS, MUSIC_LEARNING_GOALS, MUSIC_LEVELS,
  MUSIC_LESSON_FORMATS, MUSIC_PHYSICAL_VENUES, MUSIC_TEACHING_LANGUAGES,
} from '@/constants/musicRegistrationOptions';
import { ChoiceButtons, DropdownInput, Field, StepGrid, TextArea, TextInput } from './MusicFormControls';
import { useNavigate } from 'react-router-dom';

const steps = [
  { id: 'setup', label: 'Lesson setup' },
  { id: 'student', label: 'Student & lesson' },
  { id: 'schedule', label: 'Schedule & location' },
  { id: 'review', label: 'Budget & requirements' },
];

const LESSON_DURATION_MINUTES = [30, 45, 60, 90, 120];
const hoursToMinutes = (hours: number) => Math.round(hours * 60);
const minutesToHours = (minutes: number) => minutes / 60;

const initialData: MusicParentRegistrationFormData = {
  instrument: '', studentAge: '', musicLevel: '', learningGoal: '', examBoard: '', hasOwnInstrument: null,
  tuitionType: '', lessonFormat: '', physicalVenue: '', teachingLanguages: [], lessonsPerWeek: 1,
  lessonDuration: 1, state: '', district: '', areaNote: '', preferredTimes: {}, hourlyRate: 60,
  notes: '', termsAccepted: false,
};

const getErrors = (result: ReturnType<typeof parseMusicParentStep>) => {
  if (result.success) return {};
  const fieldErrors: Record<string, string> = {};
  result.error.issues.forEach((issue) => {
    const field = String(issue.path[0]);
    if (field && !fieldErrors[field]) fieldErrors[field] = issue.message;
  });
  return fieldErrors;
};

interface MusicParentRegistrationProps {
  isOpen: boolean;
  onClose: () => void;
  onCompleted?: () => void;
  presentation?: 'modal' | 'page';
  showPageBackButton?: boolean;
  successFeedback?: 'toast' | 'none';
}

const MusicParentRegistrationModal = ({
  isOpen,
  onClose,
  onCompleted,
  presentation = 'modal',
  showPageBackButton,
  successFeedback = 'toast',
}: MusicParentRegistrationProps) => {
  const navigate = useNavigate();
  const { backendUser, profile } = useAuthContext();
  const { openLoginModal, openCloseWarning } = useModal();
  const [data, setData] = useState(initialData);
  const [currentStep, setCurrentStep] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const errorsRef = useRef<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shouldResumeSubmission, setShouldResumeSubmission] = useState(false);
  const isDirty = useMemo(() => JSON.stringify(data) !== JSON.stringify(initialData), [data]);
  const update = (values: Partial<MusicParentRegistrationFormData>) => setData((current) => ({ ...current, ...values }));

  useEffect(() => {
    errorsRef.current = errors;
  }, [errors]);

  // Match Academic registration: only refresh errors already shown, and remove
  // each one as soon as that field becomes valid without revealing new errors.
  useEffect(() => {
    const currentErrors = errorsRef.current;
    if (Object.keys(currentErrors).length === 0) return;

    const result = parseMusicParentStep(currentStep, data);
    const failingFields = getErrors(result);
    const refreshedErrors = Object.fromEntries(
      Object.keys(currentErrors)
        .filter((field) => failingFields[field])
        .map((field) => [field, failingFields[field]]),
    );

    if (JSON.stringify(refreshedErrors) !== JSON.stringify(currentErrors)) {
      setErrors(refreshedErrors);
    }
  }, [currentStep, data]);

  useEffect(() => {
    if (!isOpen) return;
    const pending = getPendingRegistration();
    if (pending?.type === 'music_parent') {
      setData({ ...initialData, ...pending.formData } as MusicParentRegistrationFormData);
      setCurrentStep(steps.length - 1);
      setShouldResumeSubmission(true);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (!isOpen) {
      window.__devPanel?.unregister('music_parent');
      return;
    }
    window.__devPanel?.register({
      type: 'music_parent',
      setFormData: (value) => setData(value as MusicParentRegistrationFormData),
      setCurrentStep,
      stepCount: steps.length,
      get currentStep() { return currentStep; },
    });
    return () => window.__devPanel?.unregister('music_parent');
  }, [currentStep, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      window.__debugBridge?.unregisterFormData('music_parent');
      return;
    }
    window.__debugBridge?.registerFormData('music_parent', () => ({
      formData: data as unknown as Record<string, unknown>,
      validationErrors: errors,
      currentStep,
      stepCount: steps.length,
    }));
    return () => window.__debugBridge?.unregisterFormData('music_parent');
  }, [currentStep, data, errors, isOpen]);

  const validate = () => {
    const result = parseMusicParentStep(currentStep, data);
    const nextErrors = getErrors(result);
    setErrors(nextErrors);
    return result.success;
  };

  const safeClose = () => {
    if (isDirty && !isSubmitting) {
      openCloseWarning({ title: 'Unsaved music request', message: 'Discard the information entered in this form?', cancelText: 'Continue editing', confirmText: 'Discard', onConfirm: onClose });
      return;
    }
    onClose();
  };

  const submit = async () => {
    if (!validate()) return;
    if (backendUser && (profile?.role ?? backendUser.currentRole) !== 'parent') {
      toast.error('Please switch to a Parent/Student account before submitting.');
      return;
    }
    if (!backendUser) {
      savePendingRegistration('music_parent', data as unknown as Record<string, unknown>, presentation);
      if (presentation === 'page') {
        navigate('/music/register/auth');
        return;
      }
      onClose();
      openLoginModal({ initialMode: 'signup', initialUserType: 'parent', fromRegistration: true, registrationService: 'music' });
      return;
    }
    setIsSubmitting(true);
    try {
      await createCase({
        serviceType: 'music', tuitionType: data.tuitionType as 'physical' | 'online' | 'both',
        teachingLanguages: data.teachingLanguages, lessonsPerWeek: data.lessonsPerWeek,
        lessonDuration: data.lessonDuration, hourlyRate: data.hourlyRate,
        state: data.tuitionType === 'online' ? undefined : data.state,
        district: data.tuitionType === 'online' ? undefined : data.district,
        areaNote: data.tuitionType === 'online' ? undefined : data.areaNote || undefined,
        preferredTimes: data.preferredTimes,
        musicDetails: {
          instrument: data.instrument, studentAge: Number(data.studentAge), musicLevel: data.musicLevel,
          learningGoal: data.learningGoal, lessonFormat: data.lessonFormat,
          physicalVenue: data.tuitionType === 'online' ? undefined : data.physicalVenue || undefined,
          hasOwnInstrument: data.hasOwnInstrument ?? undefined, examBoard: data.examBoard || undefined,
          preferredDuration: data.lessonDuration, notes: data.notes || undefined,
        },
      });
      clearPendingRegistration();
      if (successFeedback === 'toast') {
        toast.success('Your music tutor request has been submitted.');
      }
      setData(initialData);
      setCurrentStep(0);
      (onCompleted ?? onClose)();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const activeRole = profile?.role ?? backendUser?.currentRole;
    if (!shouldResumeSubmission || !backendUser || activeRole !== 'parent' || isSubmitting) return;
    setShouldResumeSubmission(false);
    void submit();
  // The resume flag guarantees this runs once after authenticated state and restored data are ready.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backendUser, data, isSubmitting, profile, shouldResumeSubmission]);

  return (
    <RegistrationModalShell isOpen={isOpen} onClose={safeClose} title="Find a Music Tutor" steps={steps} currentStep={currentStep} onBack={() => { setErrors({}); setCurrentStep((s) => Math.max(0, s - 1)); }} onNext={() => { if (validate()) { setErrors({}); setCurrentStep((s) => s + 1); } }} onSubmit={submit} isSubmitting={isSubmitting} submitLabel="Submit request" themeClassName="theme-music" presentation={presentation} showPageBackButton={showPageBackButton}>
      {currentStep === 0 && <div className="space-y-6">
        <Field label="Lesson mode" error={errors.tuitionType}><TuitionModeSelector value={data.tuitionType} onChange={(tuitionType) => update({ tuitionType })} /></Field>
        <Field label="Lesson format" error={errors.lessonFormat}><ChoiceButtons options={MUSIC_LESSON_FORMATS} value={data.lessonFormat} onChange={(value) => update({ lessonFormat: value })} /></Field>
        {data.tuitionType !== 'online' && <Field label="Physical lesson venue (optional)" hint="Leave blank if you have no preference."><DropdownInput searchable={false} options={[{ value: '', label: 'No preference' }, ...MUSIC_PHYSICAL_VENUES]} value={data.physicalVenue} onChange={(value) => update({ physicalVenue: value as MusicParentRegistrationFormData['physicalVenue'] })} placeholder="No preference" /></Field>}
      </div>}
      {currentStep === 1 && <div className="space-y-6">
        <StepGrid>
          <Field label="Instrument" error={errors.instrument}><DropdownInput options={MUSIC_INSTRUMENTS} value={data.instrument} onChange={(value) => update({ instrument: String(value) })} placeholder="Select instrument" allowCustom /></Field>
          <Field label="Student age" error={errors.studentAge}><TextInput type="number" min={1} max={100} value={data.studentAge} onChange={(e) => update({ studentAge: e.target.value ? Number(e.target.value) : '' })} /></Field>
          <Field label="Current music level" error={errors.musicLevel}><DropdownInput searchable={false} options={MUSIC_LEVELS} value={data.musicLevel} onChange={(value) => update({ musicLevel: String(value) })} placeholder="Select level" /></Field>
          <Field label="Learning goal (optional)" error={errors.learningGoal}><DropdownInput searchable={false} options={[{ value: '', label: 'No preference' }, ...MUSIC_LEARNING_GOALS.map((value) => ({ value, label: value }))]} value={data.learningGoal} onChange={(value) => update({ learningGoal: String(value) })} placeholder="No preference" /></Field>
          <Field label="Exam board (optional)" error={errors.examBoard}><DropdownInput options={MUSIC_EXAM_BOARDS} value={data.examBoard} onChange={(value) => update({ examBoard: String(value) })} placeholder="No preference" allowCustom /></Field>
          <Field label="Does the student own the instrument?" error={errors.hasOwnInstrument}><ChoiceButtons options={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]} value={data.hasOwnInstrument === null ? '' : data.hasOwnInstrument ? 'yes' : 'no'} onChange={(v) => update({ hasOwnInstrument: v === 'yes' })} /></Field>
        </StepGrid>
      </div>}
      {currentStep === 2 && <div className="space-y-6">
        <StepGrid>
          <Field label="Teaching languages" error={errors.teachingLanguages}><MultiSelect searchable={false} options={MUSIC_TEACHING_LANGUAGES} value={data.teachingLanguages} onChange={(teachingLanguages) => update({ teachingLanguages })} /></Field>
          <Field label="Lessons per week"><DropdownInput searchable={false} options={[1,2,3,4,5,6,7].map((value) => ({ value, label: String(value) }))} value={data.lessonsPerWeek} onChange={(value) => update({ lessonsPerWeek: Number(value) })} /></Field>
          <Field label="Lesson duration"><DropdownInput searchable={false} options={LESSON_DURATION_MINUTES.map((value) => ({ value, label: `${value} minutes` }))} value={hoursToMinutes(data.lessonDuration)} onChange={(value) => update({ lessonDuration: minutesToHours(Number(value)) })} /></Field>
        </StepGrid>
        {data.tuitionType !== 'online' && <section className="space-y-5 border-t border-gray-200 pt-6">
          <h3 className="text-lg font-semibold text-gray-900">Lesson location</h3>
          <div className="grid gap-5 md:grid-cols-2">
            <Field label="State" error={errors.state}><DropdownInput options={STATE_OPTIONS} value={data.state} onChange={(value) => update({ state: String(value), district: '', areaNote: '' })} placeholder="Select state" /></Field>
            <Field label="District" error={errors.district}><DropdownInput disabled={!data.state} options={STATE_DISTRICTS[data.state as keyof typeof STATE_DISTRICTS] ?? []} value={data.district} onChange={(value) => update({ district: String(value) })} placeholder="Select district" /></Field>
            <div className="space-y-2 md:col-span-2">
              <div className="flex min-h-7 items-center gap-2">
                <span className="text-sm font-semibold text-gray-800">Specific Areas / Neighbourhoods (Optional)</span>
                <TooltipIcon label="Why share a general area?">
                  <p>Share a general area or neighbourhood, not your full address. This helps us match you with suitable nearby tutors more accurately while protecting your privacy.</p>
                </TooltipIcon>
              </div>
              <TextInput maxLength={80} value={data.areaNote} onChange={(e) => update({ areaNote: e.target.value })} />
              {errors.areaNote && <p role="alert" className="text-sm font-medium text-red-600">{errors.areaNote}</p>}
            </div>
          </div>
        </section>}
        <div><WeeklyTimeTable selectedTimes={data.preferredTimes} onChange={(preferredTimes) => update({ preferredTimes })} label="Preferred times" />{errors.preferredTimes && <p role="alert" className="mt-2 text-sm font-medium text-red-600">{errors.preferredTimes}</p>}</div>
      </div>}
      {currentStep === 3 && <div className="space-y-6">
        <div className="space-y-2 rounded-xl border border-primary-200 bg-primary-50/70 p-4"><div className="flex items-center justify-between"><span className="text-sm font-semibold text-primary-800">Maximum hourly budget <span className="text-red-500">*</span></span></div><SingleRangeSlider min={10} max={800} value={data.hourlyRate} onChange={(hourlyRate) => update({ hourlyRate })} unit="RM" colorTheme="primary" />{errors.hourlyRate && <p role="alert" className="text-sm font-medium text-red-600">{errors.hourlyRate}</p>}</div>
        <div className="space-y-2">
          <div className="flex min-h-7 items-center gap-2">
            <span className="text-sm font-semibold text-gray-800">Remarks (optional)</span>
            <TooltipIcon label="What can I include in remarks?">
              <p>Briefly add learning preferences, scheduling constraints, or other details that may help the tutor prepare. Keep it short.</p>
            </TooltipIcon>
          </div>
          <TextArea rows={4} maxLength={500} value={data.notes} onChange={(e) => update({ notes: e.target.value })} />
          {errors.notes && <p role="alert" className="text-sm font-medium text-red-600">{errors.notes}</p>}
        </div>
        <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-300 p-4"><input type="checkbox" checked={data.termsAccepted} onChange={(e) => update({ termsAccepted: e.target.checked })} className="mt-1 h-4 w-4 accent-primary" /><span className="text-sm text-gray-700">I agree to the <a href="/terms-tutors" target="_blank" rel="noopener noreferrer" className="font-medium text-primary underline hover:text-primary-600">Terms of Service</a> and <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="font-medium text-primary underline hover:text-primary-600">Privacy Policy</a>.</span></label>{errors.termsAccepted && <p role="alert" className="text-sm text-red-600">{errors.termsAccepted}</p>}
      </div>}
    </RegistrationModalShell>
  );
};

export default MusicParentRegistrationModal;
